"use client";

import { useCallback, useState } from "react";
import { useDebounce } from "use-debounce";

/**
 * Separate search hook for navbar search functionality.
 * Does NOT sync with URL to avoid conflicts with shop page search.
 * Used specifically for navbar dropdown search suggestions.
 * Provides clear functionality and maintains independent state.
 * Optimized for smooth UX with proper debouncing.
 */
export function useNavbarSearch() {
  // Local state only - no URL synchronization
  const [search, setSearch] = useState("");

  // Optimized debounce timing for smooth UX:
  // - 250ms provides responsive feel while reducing API calls
  // - Faster response for better user experience
  const [debouncedSearch] = useDebounce(search, 250);

  // User is actively searching when local input differs from debounced value
  // Only show loading state when search is meaningful (>= 2 characters)
  const isSearching = search !== debouncedSearch && search.length >= 2;

  // Function to clear the search input - memoized for stable reference
  const clearSearch = useCallback(() => {
    setSearch("");
  }, []);

  return {
    search,
    setSearch,
    isSearching,
    debouncedSearch,
    clearSearch,
  };
}
